import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { t, tn } from "@/i18n";
import { dayNum, fmtDayShort, todayLocal, weekday } from "@/lib/format";
import { MODE } from "@/lib/meta";
import { useUI } from "@/lib/store";
import type { Derived } from "@/lib/trip";
import { cx, IconButton } from "./ui";

export function Timeline({ d, compact }: { d: Derived; compact?: boolean }) {
  const selectedDay = useUI((s) => s.selectedDay);
  const selectDay = useUI((s) => s.selectDay);
  const [playing, setPlaying] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const today = todayLocal();

  useEffect(() => {
    if (!selectedDay) return;
    const el = scroller.current?.querySelector<HTMLElement>(`[data-day="${selectedDay}"]`);
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [selectedDay]);

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      const cur = useUI.getState().selectedDay;
      const idx = cur ? d.days.findIndex((x) => x.day === cur) : -1;
      if (idx >= d.days.length - 1) {
        setPlaying(false);
        return;
      }
      selectDay(d.days[idx + 1].day);
    }, 2600);
    return () => clearInterval(timer);
  }, [playing, d, selectDay]);

  const step = (dir: 1 | -1) => {
    const idx = selectedDay ? d.days.findIndex((x) => x.day === selectedDay) : dir > 0 ? -1 : d.days.length;
    const n = Math.min(d.days.length - 1, Math.max(0, idx + dir));
    selectDay(d.days[n].day);
  };

  return (
    <div
      className="glass flex items-stretch gap-1 rounded-2xl p-1.5"
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") step(1);
        if (e.key === "ArrowLeft") step(-1);
      }}
    >
      <div className="flex shrink-0 flex-col justify-center gap-1">
        <IconButton
          label={playing ? t("Пауза") : t("Проиграть поездку по дням")}
          className="bg-[var(--accent)] text-[var(--accent-fg)] hover:bg-[var(--accent)] hover:brightness-110"
          onClick={() => {
            if (!playing && (!selectedDay || selectedDay === d.days[d.days.length - 1]?.day)) selectDay(d.days[0].day);
            setPlaying(!playing);
          }}
        >
          {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
        </IconButton>
      </div>
      {!compact && (
        <IconButton label={t("Предыдущий день")} className="hidden self-center sm:inline-grid" onClick={() => step(-1)}>
          <ChevronLeft size={18} />
        </IconButton>
      )}
      <div ref={scroller} className="no-scrollbar flex min-w-0 flex-1 snap-x gap-1 overflow-x-auto scroll-smooth" role="listbox" aria-label={t("Дни поездки")}>
        <button
          onClick={() => selectDay(null)}
          className={cx(
            "flex shrink-0 snap-start flex-col items-center justify-center rounded-xl px-3 text-[12px] font-bold transition",
            !selectedDay ? "bg-[var(--fg)] text-[var(--bg)]" : "hover:bg-[var(--surface-2)]",
          )}
        >
          {t("Все")}
          <span className="text-[10px] font-semibold whitespace-nowrap opacity-70">{tn(d.days.length, "{n} дн.|{n} дн.|{n} дн.")}</span>
        </button>
        {d.days.map((x) => {
          const active = x.day === selectedDay;
          const isToday = x.day === today;
          const emojis = [...new Set(x.modes)].slice(0, 2).map((m) => MODE[m as keyof typeof MODE]?.emoji);
          return (
            <button
              key={x.day}
              data-day={x.day}
              role="option"
              aria-selected={active}
              onClick={() => selectDay(active ? null : x.day)}
              title={`${fmtDayShort(x.day)}${x.base ? ` · ${x.base}` : ""}`}
              className={cx(
                "group relative flex shrink-0 snap-start flex-col items-center overflow-hidden rounded-xl border pt-1.5 transition",
                compact ? "w-[52px] pb-1" : "w-[64px] pb-1.5",
                active ? "border-transparent text-white shadow-md" : "border-transparent hover:bg-[var(--surface-2)]",
              )}
              style={active ? { background: x.color } : undefined}
            >
              <span className="absolute inset-x-2 top-0 h-[3px] rounded-b" style={{ background: active ? "rgba(255,255,255,.6)" : x.color }} />
              <span className={cx("text-[10px] font-bold uppercase", active ? "opacity-90" : "text-muted")}>{weekday(x.day)}</span>
              <span className="font-display text-[17px] leading-tight font-bold">{dayNum(x.day)}</span>
              {!compact && (
                <span className={cx("w-full truncate px-1 text-center text-[10px] font-semibold", active ? "opacity-95" : "text-muted")}>{x.base ?? "—"}</span>
              )}
              <span className="flex h-3.5 items-center gap-0.5 text-[10px] leading-none">
                {emojis.length ? emojis.join("") : x.items.length ? <span className={cx("text-[10px] font-bold", active ? "" : "text-muted")}>{x.items.length}</span> : null}
              </span>
              {isToday && <span className="absolute top-1 right-1 size-1.5 rounded-full bg-[var(--danger)]" title={t("Сегодня")} />}
            </button>
          );
        })}
      </div>
      {!compact && (
        <IconButton label={t("Следующий день")} className="hidden self-center sm:inline-grid" onClick={() => step(1)}>
          <ChevronRight size={18} />
        </IconButton>
      )}
    </div>
  );
}
